/**
 * §v4.nav-counts (2026-07-02) — live item counts for the sidebar rail.
 *
 * Each "Today" work surface (Live monitor / Enquiries / Orders / Support /
 * Customers) shows how many items live behind it, on the right of the nav
 * label — replacing the old ⌘1–⌘9 hints, which were decorative only (no
 * global key handler ever shipped; same "don't promise what isn't there"
 * rationale as §v3.sidebar-search-retired).
 *
 * Design constraints:
 *  - REUSE the exact queryKey + queryFn each destination page already
 *    fires (see routePrefetch.ts), with a `select` down to the count.
 *    The cache then holds the full page payload — so the sidebar poll
 *    doubles as a permanent page-cache warmer (click → instant render),
 *    and the badge can never disagree with the page it labels.
 *  - Enquiries is the ONE exception: the page's default query runs the
 *    LLM auto-classifier (classify:"auto", up to 25 calls per request).
 *    Polling that from the rail would burn classifier budget every 30s,
 *    so the badge uses a classifier-free variant under its own key.
 *  - Counts are display-capped at 99+ by the renderer; the underlying
 *    lists are server-capped (200–500) so `length`/`count` stays cheap.
 */
import { useQueries } from "@tanstack/react-query";

import { listCustomers, listOrders, listSupportTickets } from "./api";
import { listApprovals } from "./approvals";
import { getUsageSummary } from "@/lib/billing";
import { listEnquiries } from "./conversations";
import { listLiveCalls } from "./live";
import { productForecast, type ForecastItem } from "./products";

const POLL_MS = 30_000;
/** Live calls churn fast (page itself polls at 3s); poll a bit tighter. */
const LIVE_POLL_MS = 15_000;

type CountSpec = {
  path: string;
  queryKey: readonly unknown[];
  queryFn: () => Promise<unknown>;
  select: (data: never) => number;
  refetchInterval: number;
};

// Filter literals below MUST mirror the destination pages' default
// queryKeys (kept in sync the same way routePrefetch.ts is).
const ORDERS_FILTERS = { status: "", channel: "", query: "" } as const;
const SUPPORT_FILTERS = {
  status: "",
  priority: "",
  channel: "",
  issue_type: "",
  query: "",
} as const;
const CUSTOMER_BROWSE_FILTERS = {
  gender: "all",
  city: "",
  minSpend: "",
  maxSpend: "",
  lastPurchaseFrom: "",
  lastPurchaseTo: "",
} as const;

// §stock-alert-badge (2026-07-11, Akil) — Products badges its stock ALERTS
// (out of stock / re-order now / running low), not a total. Semantics are
// acknowledge-on-visit: opening the Products page marks the current alert
// set as seen (the forecast panel calls ackStockAlerts) and the badge goes
// quiet; it only relights when an alert APPEARS or ESCALATES (the ack key
// carries the status, so low→out re-alerts). Seen-state is per operator,
// in localStorage.
const STOCK_ACK_KEY = "xpand.stockAlertsAcked.v1";
const ALERT_STATUSES = new Set(["out_of_stock", "reorder_now", "low"]);

export function stockAlertKeys(items: ForecastItem[]): string[] {
  return items
    .filter((i) => ALERT_STATUSES.has(i.status))
    .map(
      (i) =>
        `${i.status}:${String(i.product_id ?? "").trim() || i.sku.trim() || i.name}`,
    );
}

function readAckedAlerts(): Set<string> {
  try {
    const raw = window.localStorage.getItem(STOCK_ACK_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}

export function ackStockAlerts(keys: string[]): void {
  try {
    window.localStorage.setItem(STOCK_ACK_KEY, JSON.stringify(keys));
  } catch {
    /* storage unavailable — the badge just stays live */
  }
}

const SPECS: CountSpec[] = [
  {
    path: "/live-calls",
    queryKey: ["live-calls"],
    queryFn: () => listLiveCalls(),
    select: (calls: Awaited<ReturnType<typeof listLiveCalls>>) => calls.length,
    refetchInterval: LIVE_POLL_MS,
  },
  {
    path: "/enquiries",
    // Distinct key on purpose — see header comment (classifier cost).
    queryKey: ["enquiries", "nav-count"],
    queryFn: () => listEnquiries({ limit: 200 }),
    select: (r: Awaited<ReturnType<typeof listEnquiries>>) => r.count,
    refetchInterval: POLL_MS,
  },
  {
    path: "/orders",
    queryKey: ["orders", ORDERS_FILTERS],
    queryFn: () => listOrders(ORDERS_FILTERS),
    select: (r: Awaited<ReturnType<typeof listOrders>>) => r.orders.length,
    refetchInterval: POLL_MS,
  },
  {
    // §approval-wiring (2026-08-07) — the queue's whole contract is "the
    // agent prepared it and is WAITING on you", so of every badge in this
    // rail this is the one that must never lag: it reuses the Approvals
    // page's own "pending" key, and a decision from the page invalidates
    // ["approvals"] which refreshes this too.
    path: "/approvals",
    queryKey: ["approvals", "pending"],
    queryFn: () => listApprovals("pending"),
    select: (r: Awaited<ReturnType<typeof listApprovals>>) => r.count,
    refetchInterval: POLL_MS,
  },
  {
    path: "/support",
    queryKey: ["support-tickets", SUPPORT_FILTERS],
    queryFn: () => listSupportTickets(SUPPORT_FILTERS),
    select: (r: Awaited<ReturnType<typeof listSupportTickets>>) =>
      r.tickets.length,
    refetchInterval: POLL_MS,
  },
  {
    path: "/products",
    // ai=false: the badge needs counts, not the AI-written summary — no
    // LLM spend from a 2-minute sidebar poll.
    queryKey: ["stock-forecast-nav"],
    queryFn: () => productForecast({ ai: false }),
    select: (f: Awaited<ReturnType<typeof productForecast>>) => {
      const acked = readAckedAlerts();
      return stockAlertKeys(f.items).filter((k) => !acked.has(k)).length;
    },
    refetchInterval: 120_000,
  },
  {
    path: "/customers",
    queryKey: ["customers-list", "", CUSTOMER_BROWSE_FILTERS],
    queryFn: () => listCustomers({ q: "" }),
    select: (r: Awaited<ReturnType<typeof listCustomers>>) => r.count,
    refetchInterval: POLL_MS,
  },
  {
    // §usage-billing — near-limit warning: badge "1" when this cycle's
    // credits pass the 80% warn threshold, gone once topped up. Reuses
    // the page's own query key; the server caches the summary 5 min.
    path: "/usage",
    queryKey: ["billing-usage"],
    queryFn: getUsageSummary,
    select: (s: Awaited<ReturnType<typeof getUsageSummary>>) =>
      s.allowance.warning === "ok" ? 0 : 1,
    refetchInterval: 600_000,
  },
];

/**
 * Returns { "/orders": 12, "/support": 3, ... } for the given visible
 * nav paths. Only fires queries for paths actually rendered (the
 * caller passes its permission-filtered nav), so a viewer without
 * orders.view never triggers the orders fetch. Errors and loading
 * states surface as `undefined` → the badge simply doesn't render.
 */
export function useNavCounts(
  visiblePaths: ReadonlySet<string>,
): Record<string, number | undefined> {
  const specs = SPECS.filter((s) => visiblePaths.has(s.path));
  const results = useQueries({
    queries: specs.map((s) => ({
      queryKey: s.queryKey as unknown[],
      queryFn: s.queryFn,
      select: s.select as (data: unknown) => number,
      refetchInterval: s.refetchInterval,
      staleTime: 25_000,
      retry: 1,
    })),
  });
  const counts: Record<string, number | undefined> = {};
  specs.forEach((s, i) => {
    const value = results[i]?.data;
    counts[s.path] = typeof value === "number" ? value : undefined;
  });
  return counts;
}

/** 1→"1" … 99→"99", anything above → "99+". */
export function formatNavCount(n: number): string {
  return n > 99 ? "99+" : String(n);
}
